import type { DealFilter } from './types';

/**
 * The filters of board and list live in the URL
 * (`?advisorId=&propertyId=&partner=&q=&gate=1&overdue=1&noStep=1&cancelled=1&age=14`).
 */

const KEYS: Record<keyof DealFilter, string> = {
  advisorId: 'advisorId',
  propertyId: 'propertyId',
  referrerPartnerId: 'partner',
  q: 'q',
  stageAgeOver: 'age',
  gateOnly: 'gate',
  overdueOnly: 'overdue',
  noNextStep: 'noStep',
  mine: 'mine',
  includeCancelled: 'cancelled',
};

export function filterFromParams(params: URLSearchParams): DealFilter {
  const age = params.get(KEYS.stageAgeOver);
  return {
    advisorId: params.get(KEYS.advisorId) ?? undefined,
    propertyId: params.get(KEYS.propertyId) ?? undefined,
    referrerPartnerId: params.get(KEYS.referrerPartnerId) ?? undefined,
    q: params.get(KEYS.q) ?? undefined,
    stageAgeOver: age === '14' || age === '30' ? age : undefined,
    gateOnly: params.get(KEYS.gateOnly) === '1' || undefined,
    overdueOnly: params.get(KEYS.overdueOnly) === '1' || undefined,
    noNextStep: params.get(KEYS.noNextStep) === '1' || undefined,
    mine: params.get(KEYS.mine) === '1' || undefined,
    includeCancelled: params.get(KEYS.includeCancelled) === '1' || undefined,
  };
}

/** Writes the filter into the params (removing the keys that are unset); other params stay. */
export function applyFilterToParams(params: URLSearchParams, filter: DealFilter): URLSearchParams {
  const next = new URLSearchParams(params);
  const set = (key: keyof DealFilter, value: string | undefined) => {
    if (value) next.set(KEYS[key], value);
    else next.delete(KEYS[key]);
  };
  set('advisorId', filter.advisorId);
  set('propertyId', filter.propertyId);
  set('referrerPartnerId', filter.referrerPartnerId);
  set('q', filter.q);
  set('stageAgeOver', filter.stageAgeOver);
  set('gateOnly', filter.gateOnly ? '1' : undefined);
  set('overdueOnly', filter.overdueOnly ? '1' : undefined);
  set('noNextStep', filter.noNextStep ? '1' : undefined);
  set('mine', filter.mine ? '1' : undefined);
  set('includeCancelled', filter.includeCancelled ? '1' : undefined);
  return next;
}

/** Number of filters set (the „Filter · N aktiv" label); the search is not a filter. */
export function activeFilterCount(filter: DealFilter): number {
  return [
    filter.advisorId,
    filter.propertyId,
    filter.referrerPartnerId,
    filter.stageAgeOver,
    filter.gateOnly,
    filter.overdueOnly,
    filter.noNextStep,
    filter.includeCancelled,
  ].filter(Boolean).length;
}
